import React, { useCallback, useMemo, useRef, useState } from 'react';

export type RangePreset = '24h' | '7d' | '30d' | '90d' | 'custom';

/** پارس تاریخ `YYYY-MM-DD` در منطقهٔ زمانی محلی (نه UTC-midnight). */
const parseLocalDate = (s: string, endOfDay: boolean): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = endOfDay
    ? new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 999)
    : new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function useDateRange(initial: RangePreset = '7d') {
  const [preset, setPreset] = useState<RangePreset>(initial);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  // شمارنده برای بازمحاسبهٔ «الان» — دکمهٔ «بروزرسانی» صفحه‌ها refresh() را صدا می‌زند
  const [refreshKey, setRefreshKey] = useState(0);
  // آخرین بازهٔ معتبر — تا وقتی هر دو ورودی سفارشی پر نشده‌اند، همین برگردانده می‌شود
  const lastValidRef = useRef<{ from: string; to: string } | null>(null);

  const range = useMemo(() => {
    const now = new Date();
    if (preset === 'custom') {
      const from = customFrom ? parseLocalDate(customFrom, false) : null;
      const to = customTo ? parseLocalDate(customTo, true) : null;
      if (from && to && from.getTime() <= to.getTime()) {
        const r = { from: from.toISOString(), to: to.toISOString() };
        lastValidRef.current = r;
        return r;
      }
      // یکی از ورودی‌ها خالی/نامعتبر است → آخرین بازهٔ معتبر حفظ می‌شود
      if (lastValidRef.current) return lastValidRef.current;
    }
    let from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    if (preset === '24h') from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    else if (preset === '30d') from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    else if (preset === '90d') from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    const r = { from: from.toISOString(), to: now.toISOString() };
    lastValidRef.current = r;
    return r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, refreshKey]);

  /** «الان» را دوباره محاسبه می‌کند (بازهٔ preset جلو می‌آید) و صفحه را وادار به fetch مجدد می‌کند. */
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range, refresh, refreshKey };
}

/** خطای بارگیری با دکمهٔ تلاش مجدد — الگوی مشترک همهٔ صفحه‌های آنالیتیکس. */
export const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="analytics-empty" style={{ color: 'var(--color-error-dark)' }}>
    {message}
    <button onClick={onRetry} style={{ marginInlineStart: 10 }}>
      تلاش مجدد
    </button>
  </div>
);

export const RangeSelector: React.FC<{
  preset: RangePreset;
  setPreset: (p: RangePreset) => void;
  customFrom: string;
  setCustomFrom: (s: string) => void;
  customTo: string;
  setCustomTo: (s: string) => void;
}> = ({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo }) => (
  <>
    <select value={preset} onChange={(e) => setPreset(e.target.value as RangePreset)}>
      <option value="24h">۲۴ ساعت اخیر</option>
      <option value="7d">۷ روز اخیر</option>
      <option value="30d">۳۰ روز اخیر</option>
      <option value="90d">۹۰ روز اخیر</option>
      <option value="custom">سفارشی</option>
    </select>
    {preset === 'custom' && (
      <>
        <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
        <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
      </>
    )}
  </>
);

export const fa = (n: number | undefined | null): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '۰';
  return new Intl.NumberFormat('fa-IR').format(Math.round(n));
};

export const faDecimal = (n: number, digits = 1): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '۰';
  return new Intl.NumberFormat('fa-IR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
};

export const fmtDuration = (sec: number): string => {
  if (!sec || sec < 0) return '۰ ثانیه';
  if (sec < 60) return `${fa(sec)} ثانیه`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${fa(m)} دقیقه ${s ? fa(s) + ' ثانیه' : ''}`.trim();
  const h = Math.floor(m / 60);
  return `${fa(h)} ساعت ${fa(m % 60)} دقیقه`;
};

export const fmtDate = (iso: string | Date | undefined): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'short' }).format(d);
  } catch {
    return '';
  }
};

/**
 * Decode safe برای نمایش مسیرهای فارسی. اگر مسیر در DB قدیمی encoded ذخیره شده،
 * در UI به شکل خوانا (مثل `/محصول/...`) نشان داده می‌شود.
 */
export const decodePath = (p: string | undefined | null): string => {
  if (!p) return '';
  try {
    return decodeURIComponent(p);
  } catch {
    return p;
  }
};

export const fmtTime = (iso: string | Date | undefined): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('fa-IR', { timeStyle: 'short' }).format(d);
  } catch {
    return '';
  }
};
